import React, { ReactNode } from "react";

export interface CustomButtonProps {
  theme?: "green" | "dark" | "blue" | "red" | "grey" | "lightgrey" | "lightGreen";
  title?: string;
  customColumnStyle?: string;
  customButtonStyle?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  circular?: boolean;
  icon?: any;
  iconSize?: "mini" | "tiny" | "small" | "large" | "big" | "huge" | "massive";
  buttonOutLine?: boolean;
  iconWithText?: boolean;
  className?: string;
  bounceEnable?: boolean;
  iconChild?: ReactNode;
  iconChildCustom?: string;
  iconChildTextCustom?: string;
  iconChildMainCustomContainer?: string;
  customBase?: string;
  customOutLine?: string;
  testId?: string;
  wrapInColumn?: boolean;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  theme = "dark",
  title = "",
  customColumnStyle = "",
  customButtonStyle = "",
  onClick,
  type = "button",
  disabled = false,
  circular = false,
  icon,
  iconSize = "large",
  buttonOutLine = false,
  iconWithText = false,
  bounceEnable = false,
  className = "",
  iconChild,
  iconChildCustom = "",
  iconChildTextCustom = "",
  iconChildMainCustomContainer = "",
  customBase = "",
  customOutLine = "",
  testId,
  wrapInColumn = true
}) => {
  // Get icon size classes
  const getIconSizeClass = () => {
    const sizeMap: Record<string, string> = {
      mini: "tw-w-3 tw-h-3",
      tiny: "tw-w-4 tw-h-4",
      small: "tw-w-5 tw-h-5",
      large: "tw-w-8 tw-h-8",
      big: "tw-w-10 tw-h-10",
      huge: "tw-w-12 tw-h-12",
      massive: "tw-w-16 tw-h-16"
    };
    return sizeMap[iconSize] || sizeMap.large;
  };

  // Get theme classes with Tailwind
  const getThemeClasses = () => {
    const baseClasses =
      "tw-px-2 tw-py-2.5 tw-font-medium tw-transition-all tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2";

    if (disabled) {
      const disabledOutlineClasses = buttonOutLine
        ? "tw-bg-transparent tw-border-2 tw-border-gray-300 tw-text-gray-400 tw-cursor-not-allowed"
        : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed";
      return `${baseClasses} ${disabledOutlineClasses} ${customBase}`;
    }

    const themeConfig: Record<string, { solid: string; outline: string }> = {
      green: {
        solid:
          "tw-bg-green-600 tw-text-white hover:tw-bg-green-700 active:tw-bg-green-800 focus:tw-ring-green-500",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-green-600 tw-text-green-600 hover:tw-bg-green-50 active:tw-bg-green-100 focus:tw-ring-green-500"
      },
      dark: {
        solid: "tw-bg-gray-800 tw-text-white hover:tw-bg-gray-900 active:tw-bg-black focus:tw-ring-gray-700",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-gray-800 tw-text-gray-800 hover:tw-bg-gray-50 active:tw-bg-gray-100 focus:tw-ring-gray-700"
      },
      blue: {
        solid: "tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 active:tw-bg-blue-800 focus:tw-ring-blue-500",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-blue-600 tw-text-blue-600 hover:tw-bg-blue-50 active:tw-bg-blue-100 focus:tw-ring-blue-500"
      },
      red: {
        solid: "tw-bg-red-600 tw-text-white hover:tw-bg-red-700 active:tw-bg-red-800 focus:tw-ring-red-500",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-red-600 tw-text-red-600 hover:tw-bg-red-50 active:tw-bg-red-100 focus:tw-ring-red-500"
      },
      grey: {
        solid: "tw-bg-gray-500 tw-text-white hover:tw-bg-gray-600 active:tw-bg-gray-700 focus:tw-ring-gray-400",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-gray-500 tw-text-gray-500 hover:tw-bg-gray-50 active:tw-bg-gray-100 focus:tw-ring-gray-400"
      },
      lightGreen: {
        solid: "tw-bg-white-100 tw-text-green-400 tw-cursor-not-allowed tw-border tw-border-gray-100",
        outline: "tw-bg-transparent tw-border-2 tw-border-gray-100 tw-text-green-200 tw-cursor-not-allowed"
      },
      lightgrey: {
        solid: "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300 active:tw-bg-gray-400 focus:tw-ring-gray-200",
        outline:
          "tw-bg-transparent tw-border-2 tw-border-gray-200 tw-text-gray-700 hover:tw-bg-gray-100 active:tw-bg-gray-200 focus:tw-ring-gray-200"
      }
    };

    const selectedTheme = themeConfig[theme] || themeConfig.green;
    const themeClass = buttonOutLine
      ? `${selectedTheme.outline} ${customOutLine}`
      : `${selectedTheme.solid} ${customBase}`;

    return `${baseClasses} ${themeClass}`;
  };

  // Get shape classes
  const getShapeClasses = () => {
    if (circular) {
      return "tw-rounded-full";
    }
    return icon && !iconWithText && circular ? "tw-rounded-full tw-p-2" : "tw-rounded-md";
  };

  // Render button content
  const renderButtonContent = () => {
    if (icon && !iconWithText) {
      return iconChild ? (
        <label className={`tw-inline-flex tw-items-center tw-justify-center ${getIconSizeClass()}`}>
          {iconChild}
        </label>
      ) : null;
    }

    if (iconWithText) {
      return (
        <div className={`tw-flex tw-items-center tw-gap-2 ${iconChildMainCustomContainer}`}>
          <div className={`tw-inline-flex tw-items-center ${iconChildCustom}`}>{iconChild}</div>
          <label className={`${iconChildTextCustom}`}>{title}</label>
        </div>
      );
    }

    return title;
  };

  // Button element
  const buttonElement = (
    <button
      className={`
        ${getThemeClasses()}
        ${getShapeClasses()}
        ${customButtonStyle}
        ${className}
        tw-w-full tw-flex tw-items-center tw-justify-center
      `}
      onClick={onClick}
      type={type}
      disabled={disabled}
      data-testid={testId}
    >
      {renderButtonContent()}
    </button>
  );

  // Optionally wrap in column div for backward compatibility
  if (wrapInColumn) {
    return (
      <div className={`${customColumnStyle} ${bounceEnable ? "bouncing-button" : ""}`}>
        {buttonElement}
      </div>
    );
  }

  return buttonElement;
};
